<template>
  <v-container fluid class="fill-height">
    <v-row justify="center" align-items="center">
      <v-col cols="12" class="d-flex justify-center">
        <v-card
          class="w-100"
          :style="{ maxWidth: $vuetify.display.mdAndUp ? '700px' : '100%' }"
          data-testid="login-form-card">
          <div data-testid="login-form">
            <v-card-title class="text-h6 pa-4 d-flex justify-space-between align-center">
              <span>Login</span>
              <v-btn
                variant="text"
                size="small"
                color="primary"
                to="/"
                class="text-caption"
                data-testid="login-back-home-button"
              >
                <v-icon start size="small">mdi-arrow-left</v-icon>
                Back to Home
              </v-btn>
            </v-card-title>
            <v-divider/>
            <v-card-text>
              <div class="mb-4">
                <p class="text-subtitle-2 text-medium-emphasis mb-3">Select Identification Type</p>
                
                <!-- Desktop/tablet layout: horizontal buttons with icons and multi-line text -->
                <v-row class="d-none d-sm-flex mb-0 justify-center" no-gutters>
                  <v-col 
                    v-for="(type, index) in idTypes" 
                    :key="type.key"
                    cols="auto"
                    :class="['mb-2', { 'me-2': index < idTypes.length - 1 }]"
                  >
                    <v-btn
                      variant="flat"
                      color="selectable"
                      :class="{'h-auto pa-3': true, 'selected': idType === type.key}"
                      style="min-width: 120px;"
                      :data-testid="`login-idtype-${type.key}-button`"
                      @click="idType = type.key"
                    >
                      <div class="d-flex flex-column align-center text-center">
                        <v-icon 
                          :icon="type.icon"
                          size="24"
                          class="mb-2"
                        />
                        <span class="text-caption" style="line-height: 1.2; white-space: normal;">
                          {{ type.name }}
                        </span>
                      </div>
                    </v-btn>
                  </v-col>
                </v-row>

                <!-- Mobile layout: vertical buttons with icons and single-line text -->
                <div class="d-flex d-sm-none flex-column ga-2">
                  <v-btn
                    v-for="type in idTypes"
                    :key="type.key"
                    variant="flat"
                    color="selectable"
                    :class="{'justify-start pa-3': true, 'selected': idType === type.key}"
                    :data-testid="`login-idtype-${type.key}-button`"
                    @click="idType = type.key"
                  >
                    <template #prepend>
                      <v-icon :icon="type.icon" size="20" />
                    </template>
                    <span class="text-truncate">{{ type.name }}</span>
                  </v-btn>
                </div>
              </div>
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="formId"
                    :label="idTypes.find(t => t.key === idType)?.name"
                    data-testid="login-form-id-input"
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="formPassword"
                    label="Password"
                    :type="passwordVisible ? 'text' : 'password'"
                    :append-inner-icon="passwordVisible ? 'mdi-eye' : 'mdi-eye-off'"
                    data-testid="login-form-password-input"
                    @click:append-inner="passwordVisible = !passwordVisible"
                    @keyup.enter="handleSubmit"
                  />
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                variant="flat"
                color="primary"
                :loading="isLoading"
                :disabled="isLoading"
                data-testid="login-form-submit-button"
                @click="handleSubmit"
              >
                {{ isLoading ? 'Logging in...' : 'Login' }}
              </v-btn>
            </v-card-actions>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { post } from '~/composables/useApi';
import { useAuth } from '~/composables/useAuth';
import { useAppTheme } from '~/composables/useTheme';
import { parseAccountNumber, parsePhone, parseIdNumber } from '~/utils/formatters';
import type { User } from '#shared/data/Account';
import { IdType } from '#shared/enums';

// SEO for login page
useHead({
  title: 'Login - INDA Assessment Platform',
  meta: [
    {
      name: 'description',
      content: 'Login to your INDA account to access assessments, reports, and manage your homeschool compliance documentation.'
    }
  ]
});


const idTypes = ref([
  { name: 'Account Number', key: IdType.ACCOUNT_NUMBER, icon: 'mdi-account-circle' },
  { name: 'E-Mail', key: IdType.MAIL, icon: 'mdi-email' },
  { name: 'Phone', key: IdType.PHONE, icon: 'mdi-phone' },
  { name: 'South African ID', key: IdType.ID_NUMBER, icon: 'mdi-card-account-details' },
  { name: 'Passport Number', key: IdType.PASSPORT, icon: 'mdi-passport' },
]);

const idType = ref(IdType.ACCOUNT_NUMBER);
const isLoading = ref(false);
const passwordVisible = ref(false);

// Initialize theme composable at setup level
const { initializeUserTheme } = useAppTheme();

// Form values
const formId = ref('');
const formPassword = ref('');

// Handle submit button click
const handleSubmit = async () => {
  if (formId.value && formPassword.value) {
    await submit({
      id: formId.value,
      password: formPassword.value
    });
  }
};

// Login function
const submit = async (values: { id: string; password: string }) => {
  const formValues = values;
  try {
    isLoading.value = true;

    // Clear any existing user session data before attempting login
    const auth = useAuth();

    // Make sure the system does not accidentally detect an old role which had expired
    auth.clearUserData();

    // Prepare login data to match the server LoginRequest model
    let parsedId: string | number = formValues.id;
    
    // Parse the ID based on the selected type
    if (idType.value === IdType.ACCOUNT_NUMBER) {
      parsedId = parseAccountNumber(formValues.id);
    } else if (idType.value === IdType.PHONE) {
      parsedId = parsePhone(formValues.id);
    } else if (idType.value === IdType.ID_NUMBER) {
      parsedId = parseIdNumber(formValues.id);
    }
    
    const loginData = {
      idType: idType.value, // This will be one of: mail, idNumber, passport, phone, accountNumber
      id: parsedId, // The actual identifier value
      password: formValues.password
    };

    const response = await post<User>('public/auth/login', loginData);
   
    await auth.login(response);
    
    // Initialize user theme preferences after successful login
    initializeUserTheme();
  } finally {
    isLoading.value = false;
  }
};

</script>
